import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { fetchMortgageTypes, fetchNhg } from './services/interest-prodviders-list-service';
import { useForm } from 'react-hook-form';
import {
  ApiMortgageTypesResponse,
  ApiNhgResponse,
  InterestProvidersListFiltersFormFields,
  InterestProvidersListMortgageTypes
} from '../../../@types';
import {
  getParamsFromPath,
  rewriteIntroText,
  rewriteLanguageSwitch,
  rewritePath,
  translateMortgageType
} from './utils';
import { DynamicInput, FormElementDynamicInputTyped } from '../../shared/form-elements';
import { useInterestProvidersListFiltersFormFields } from './config';
import { validationMessages } from '../../shared/validation/validation-messages';
import { InterestProvidersContext } from './context';
import { mainMediaMobile, scrollTo, utilToLocaleString } from '../../../utils';
import { AppPopOver } from '../../shared/general';
import { FormattedMessage, useIntl } from 'react-intl';
import { typesLang } from '../../../@types/shared/types';
import { Button, Link } from '@chakra-ui/react';
import useMediaQuery from '../../../hooks/use-media';
import { SharedIconClose } from '../../shared/icons';

interface InterestProvidersListFiltersProps {
  date: string;
}

export const InterestProvidersListFilters: FunctionComponent<InterestProvidersListFiltersProps> = ({
  date
}) => {
  const intl = useIntl();
  const isMobile = useMediaQuery(mainMediaMobile);
  const interestProvidesListFiltersFormFields = useInterestProvidersListFiltersFormFields(intl);
  const { setFilters, isLoading, setHideList } = useContext(InterestProvidersContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [metaDataFetched, setMetaDataFetched] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(!isMobile ? true : false);
  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [nhg, setNhg] = useState<ApiNhgResponse>({} as ApiNhgResponse);
  const [mortgageTypes, setMortgageTypes] = useState<ApiMortgageTypesResponse>(
    [] as ApiMortgageTypesResponse
  );

  const nhgLink = intl.formatMessage({
    id: 'filters.interestTariff.tooltip.nhgLink',
    description:
      '<a href="https://www.hypotheker.nl/begrippenlijst/huis-kopen/nationale-hypotheek-garantie/" class="text-white underline-offset-2 underline">Nationale Hypotheek Garantie</a>'
  });

  const marketValueLink = intl.formatMessage({
    id: 'filters.interestTariff.tooltip.marketValueLink',
    description: `<a href="https://www.hypotheker.nl/begrippenlijst/huis-kopen/marktwaarde/" class="text-white underline-offset-2 underline">marktwaarde</a>`
  });

  const tooltipText = intl.formatMessage(
    { id: 'filters.interestTariff.tooltip' },
    {
      nhg: `€ ${utilToLocaleString(
        nhg?.existingBuilding,
        intl.locale === 'nl' ? 'nl-NL' : 'en-US'
      )}`,
      nhgEnergy: `€ ${utilToLocaleString(
        nhg?.withEnergyEfficiencyMeasures,
        intl.locale === 'nl' ? 'nl-NL' : 'en-US'
      )}`,
      nhgLink: nhgLink,
      marketValueLink: marketValueLink
    }
  );

  const getDefaultValues = (): InterestProvidersListFiltersFormFields => {
    return {
      mortgageType: interestProvidesListFiltersFormFields.mortgageType
        .defaultValue as InterestProvidersListMortgageTypes,
      mortgageTerm: interestProvidesListFiltersFormFields.mortgageTerm.defaultValue,
      interestTariff: interestProvidesListFiltersFormFields.interestTariff.defaultValue,
      buildingType: interestProvidesListFiltersFormFields.buildingType.defaultValue,
      energyLabel: interestProvidesListFiltersFormFields.energyLabel.defaultValue
    };
  };

  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useForm<InterestProvidersListFiltersFormFields>({
    mode: 'onBlur',
    defaultValues: getDefaultValues()
  });

  const urlParams = getParamsFromPath();
  const { mortgageType, mortgageTerm, interestTariff, buildingType, energyLabel } = watch();

  const showPayoff = () => {
    if (!isMobile) {
      return true;
    }

    if (!showFilters) {
      return true;
    }
  };

  const toggleFilters = (show: boolean) => {
    setShowFilters(show);
    setHideList(isMobile && show);
    if (!show) {
      scrollTo('.react-tools');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const results = await Promise.all([fetchNhg(), fetchMortgageTypes()]);
        setNhg(results[0]);
        setMortgageTypes(translateMortgageType(results[1], intl.locale as typesLang));
        setTimeout(() => {
          if (urlParams) {
            setValue('mortgageType', urlParams.mortgageType, {
              shouldValidate: false,
              shouldDirty: false,
              shouldTouch: false
            });
            setValue('mortgageTerm', urlParams.mortgageTerm, {
              shouldValidate: false,
              shouldDirty: false,
              shouldTouch: false
            });
          } else {
            setValue('mortgageType', results[1][0].mortgageKey);
          }
          setLoading(false);
          setMetaDataFetched(true);
        }, 500);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && metaDataFetched) {
      const filters = { mortgageType, mortgageTerm, interestTariff, buildingType, energyLabel };
      setFilters(filters);
      if (firstLoad) {
        rewritePath(filters, intl.locale as typesLang);
        rewriteIntroText(filters, intl.locale as typesLang);
      } else {
        setFirstLoad(true);
      }
      rewriteLanguageSwitch(filters, intl.locale as typesLang);
    }
  }, [
    mortgageType,
    mortgageTerm,
    interestTariff,
    buildingType,
    energyLabel,
    loading,
    metaDataFetched
  ]);

  useEffect(() => {
    setShowFilters(!isMobile);
    setHideList(false);
  }, [isMobile]);

  return (
    <>
      {showPayoff() && (
        <div className={`mb-8 md:flex md:justify-between`}>
          <div className={`flex justify-between items-center mb-4 md:mb-0`}>
            <div className={`text-[20px]`}>
              <FormattedMessage
                id={`filters.payoff.fortyProviders`}
                defaultMessage={`40 aanbieders`}
              />
            </div>
            {isMobile && (
              <div>
                <Link variant={`underlined`} onClick={() => toggleFilters(true)}>
                  <FormattedMessage id={`filters.showFilters`} defaultMessage={`Filteren`} />
                </Link>
              </div>
            )}
          </div>
          <div className={`md:text-[20px]`}>
            <FormattedMessage
              id={`filters.payoff.lastUpdated`}
              defaultMessage={`Rentetarieven laatst bijgewerkt op {date}`}
              values={{
                date: date
              }}
            />
          </div>
        </div>
      )}

      {showFilters && (
        <div
          className={`grid grid-cols-1 md:grid-cols-5 gap-4 bg-white md:bg-transparent left-0 p-10 md:p-4 md:p-0 ${
            isMobile && 'absolute w-[100vw] rounded-3xl shadow-[0px_4px_50px_0px_#00000040]'
          }`}>
          {isMobile && (
            <div className={`flex justify-between`}>
              <div className={`text-3xl font-bold`}>Filter resultaten</div>
              <SharedIconClose onClick={() => toggleFilters(false)} />
            </div>
          )}

          <DynamicInput
            variant={`interestRateFilter`}
            id={interestProvidesListFiltersFormFields.mortgageType.fieldName}
            label={interestProvidesListFiltersFormFields.mortgageType.label}
            type={interestProvidesListFiltersFormFields.mortgageType.type}
            validation={{ required: validationMessages.required }}
            error={errors.mortgageType}
            defaultValue={interestProvidesListFiltersFormFields.mortgageType.defaultValue}
            options={mortgageTypes.map((a) => {
              return { label: a.mortgageName, value: a.mortgageKey };
            })}
            register={register}
          />

          <FormElementDynamicInputTyped<InterestProvidersListFiltersFormFields>
            variant={`interestRateFilter`}
            formFields={interestProvidesListFiltersFormFields}
            id={'mortgageTerm'}
            register={register}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          <DynamicInput
            variant={`interestRateFilter`}
            id={interestProvidesListFiltersFormFields.interestTariff.fieldName}
            label={interestProvidesListFiltersFormFields.interestTariff.label}
            type={interestProvidesListFiltersFormFields.interestTariff.type}
            validation={{ required: validationMessages.required }}
            error={errors.interestTariff}
            defaultValue={interestProvidesListFiltersFormFields.interestTariff.defaultValue}
            options={interestProvidesListFiltersFormFields.interestTariff.options}
            register={register}>
            <AppPopOver variant={`blue`} text={tooltipText}></AppPopOver>
          </DynamicInput>

          <FormElementDynamicInputTyped<InterestProvidersListFiltersFormFields>
            variant={`interestRateFilter`}
            formFields={interestProvidesListFiltersFormFields}
            id={'buildingType'}
            register={register}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          <FormElementDynamicInputTyped<InterestProvidersListFiltersFormFields>
            variant={`interestRateFilter`}
            formFields={interestProvidesListFiltersFormFields}
            id={'energyLabel'}
            register={register}
            errors={errors}
            values={watch()}
          />

          {isMobile && (
            <Button
              className={`w-full mt-5`}
              colorScheme={`orange`}
              disabled={isLoading}
              onClick={() => toggleFilters(false)}>
              <FormattedMessage
                id={`filters.interestRate.applyFilters`}
                defaultMessage={`Filter resultaten`}
              />
            </Button>
          )}
        </div>
      )}
    </>
  );
};
